import React, { useState, useEffect, useCallback } from "react";
import { oxpayAPI } from "@/lib/oxpay";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "react-toastify";
import {
  DollarSign,
  AlertCircle,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  RefreshCw,
} from "lucide-react";

const Prefund = () => {
  const [prefundData, setPrefundData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [apiStatus, setApiStatus] = useState("connecting"); // connecting, connected, error

  const fetchPrefundData = useCallback(async () => {
    try {
      setApiStatus("connecting");

      const response = await oxpayAPI.getPrefundBalance();

      if (response?.headers?.status_code === "error") {
        setApiStatus("error");
        throw new Error(
          response?.body?.description || response?.body?.title || "API Error",
        );
      }

      if (response?.body && !Array.isArray(response.body)) {
        const prefundInfo = {
          current_balance: parseFloat(response.body.fund_balance) || 0,
          company_id: response.body.company_id,
          last_updated: response.body.updated_at || new Date().toISOString(),
          total_deposited: 0,
          total_withdrawn: 0,
        };

        setPrefundData(prefundInfo);
        setApiStatus("connected");
      } else {
        throw new Error("Invalid response from OxPay API");
      }
    } catch (error) {
      console.error("Error fetching prefund data:", error);
      setApiStatus("error");
      toast.error("Failed to fetch balance");
    }
  }, []);

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await oxpayAPI.getPrefundHistory();

      if (response?.headers?.status_code === "error") {
        throw new Error(
          response?.body?.description || response?.body?.title || "API Error",
        );
      }

      const rows = Array.isArray(response?.body) ? response.body : [];
      const mapped = rows.map((row) => {
        const isCredit =
          String(row.sub_type || "").toLowerCase() === "credit" ||
          String(row.type || "").toLowerCase() === "deposit";
        return {
          id: row.id,
          created_at: row.date_added,
          type: isCredit ? "deposit" : "withdrawal",
          amount: parseFloat(row.topup_amount) || 0,
          reference: row.payment_ref_no || "-",
          balance_after: parseFloat(row.new_prefund_balance) || 0,
          notes:
            row.status === "success"
              ? `Old: ${row.old_prefund_balance ?? "-"}`
              : row.status || "-",
        };
      });
      setTransactions(mapped);
    } catch (error) {
      console.error("Error fetching prefund transactions:", error);
      toast.error("Failed to load transaction history");
      setTransactions([]);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchPrefundData(), fetchTransactions()]);
    } finally {
      setLoading(false);
    }
  }, [fetchPrefundData, fetchTransactions]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchPrefundData(), fetchTransactions()]);
      toast.success("Data refreshed from OxPay");
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.error("Failed to refresh data");
    } finally {
      setRefreshing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-0">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Prefund Balance</h2>
              <p className="text-muted-foreground">
                View OxPay merchant prefund balance
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="flex items-center gap-1">
                <DollarSign className="h-3 w-3" />
                Company:{" "}
                {prefundData?.company_id
                  ? `${prefundData.company_id.slice(0, -4)}****`
                  : "Loading..."}
              </Badge>
              <Button
                variant="outline"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
                />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Balance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Current Balance</p>
                <p className="text-3xl font-bold">
                  {formatCurrency(prefundData?.current_balance)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Last updated: {formatDate(prefundData?.last_updated)}
                </p>
              </div>
              <div className="rounded-full bg-primary/10 p-3">
                <Wallet className="h-6 w-6 text-primary" />
              </div>
            </div>
            {prefundData?.current_balance < 50000 && (
              <div className="mt-3 flex items-center gap-1 text-xs text-orange-600">
                <AlertCircle className="h-3 w-3" />
                Low balance warning - Consider adding funds
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">API Status</p>
                <p
                  className={`text-lg font-semibold ${
                    apiStatus === "connected"
                      ? "text-green-600"
                      : apiStatus === "error"
                        ? "text-red-600"
                        : "text-yellow-600"
                  }`}
                >
                  {prefundData?.current_balance !== undefined
                    ? "Connected"
                    : apiStatus === "error"
                      ? "Connection Failed"
                      : "Checking Connection..."}
                </p>
                {/* <p className="text-xs text-muted-foreground mt-1">
                  {apiStatus === "error" && lastError ? (
                    <span className="text-red-600">{lastError}</span>
                  ) : (
                    "Auto-refresh every 30 seconds"
                  )}
                </p> */}
              </div>
              <div
                className={`rounded-full p-3 ${
                  apiStatus === "connected"
                    ? "bg-green-100 dark:bg-green-900"
                    : apiStatus === "error"
                      ? "bg-red-100 dark:bg-red-900"
                      : "bg-yellow-100 dark:bg-yellow-900"
                }`}
              >
                {apiStatus === "error" ? (
                  <AlertCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                ) : (
                  <DollarSign
                    className={`h-6 w-6 ${
                      apiStatus === "connected"
                        ? "text-green-600 dark:text-green-400"
                        : "text-yellow-600 dark:text-yellow-400"
                    }`}
                  />
                )}
              </div>
            </div>
            {/* <div className="mt-3 text-xs text-muted-foreground">
              Company ID:{" "}
              {prefundData?.company_id
                ? `${prefundData.company_id.slice(0, -4)}****`
                : "N/A"}
              {prefundData?.isStale && (
                <Badge variant="outline" className="ml-2 text-xs">
                  Cached Data
                </Badge>
              )}
            </div> */}
          </CardContent>
        </Card>
      </div>

      {/* Transaction History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Transaction History</h3>
            <Badge variant="outline">
              <Calendar className="h-3 w-3 mr-1" />
              OxPay prefund history
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Balance After</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="text-muted-foreground">
                        No transactions recorded yet
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell>{formatDate(tx.created_at)}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            tx.type === "deposit" ? "secondary" : "destructive"
                          }
                          className="flex items-center gap-1 w-fit"
                        >
                          {tx.type === "deposit" ? (
                            <ArrowDownRight className="h-3 w-3" />
                          ) : (
                            <ArrowUpRight className="h-3 w-3" />
                          )}
                          {tx.type === "deposit" ? "Deposit" : "Withdrawal"}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {tx.type === "deposit" ? "+" : "-"}
                        {formatCurrency(tx.amount)}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {tx.reference}
                      </TableCell>
                      <TableCell>{formatCurrency(tx.balance_after)}</TableCell>
                      <TableCell className="max-w-xs truncate">
                        {tx.notes || "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Prefund;
